import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowRight, FaCheck, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import imageLoader from '../utils/imageLoader';

const HomePage = () => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const heroImages = imageLoader.getHeroImages('home');
  
  // Otomatik slide değişimi için
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prevSlide => (prevSlide + 1) % heroImages.length);
    }, 5000); // Her 5 saniyede bir otomatik değişim
    
    return () => clearInterval(timer); // Component unmount olduğunda timer'ı temizle
  }, [heroImages.length]);
  
  // Bir önceki görsele geçiş
  const prevSlide = () => {
    setCurrentSlide(prevSlide => 
      prevSlide === 0 ? heroImages.length - 1 : prevSlide - 1
    );
  };
  
  // Bir sonraki görsele geçiş
  const nextSlide = () => {
    setCurrentSlide(prevSlide => 
      (prevSlide + 1) % heroImages.length
    );
  };
  
  return (
    <>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-blue-800 text-white">
        <div className="container mx-auto px-4 py-20 md:py-32">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-10 md:mb-0">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
                {t('home.hero.title')}
              </h1>
              <p className="text-lg md:text-xl mb-8">
                {t('home.hero.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/products" className="bg-secondary hover:bg-yellow-600 text-white font-bold py-3 px-6 rounded-md transition-colors duration-300 text-center group">
                  {t('home.hero.browseButton')}
                  <FaArrowRight className="ml-2 inline-block transform group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/contact" className="bg-white hover:bg-gray-100 text-primary font-bold py-3 px-6 rounded-md transition-colors duration-300 text-center">
                  {t('home.hero.contactButton')}
                </Link>
              </div>
            </div>
            <div className="md:w-1/2">
              <div className="relative">
                {/* Hero Slideshow */}
                <div className="bg-gray-300 h-64 md:h-80 lg:h-96 rounded-lg overflow-hidden shadow-xl">
                  {heroImages.map((imgUrl, index) => (
                    <img
                      key={index}
                      className={`absolute w-full h-full object-cover rounded-lg transition-opacity duration-1000 ${currentSlide === index ? 'opacity-100' : 'opacity-0'}`}
                      src={imgUrl}
                      alt={`${t('home.hero.imageAlt')} ${index + 1}`}
                      onError={(e) => {
                        // Fallback if image fails to load
                        e.target.src = 'https://via.placeholder.com/800x600?text=ÇelikSac+Hero+Image';
                      }}
                    />
                  ))}
                  
                  {/* Slide Navigation Controls */}
                  <div className="absolute inset-0 flex items-center justify-between px-4">
                    <button 
                      onClick={prevSlide} 
                      className="bg-black/40 hover:bg-black/60 text-white p-2 rounded-full transition-colors"
                      aria-label="Previous slide"
                    >
                      <FaChevronLeft size={20} />
                    </button>
                    <button 
                      onClick={nextSlide} 
                      className="bg-black/40 hover:bg-black/60 text-white p-2 rounded-full transition-colors"
                      aria-label="Next slide"
                    >
                      <FaChevronRight size={20} />
                    </button>
                  </div>
                  
                  {/* Slide Indicators */}
                  <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2">
                    {heroImages.map((_, index) => (
                      <button 
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`w-3 h-3 rounded-full transition-colors ${currentSlide === index ? 'bg-white' : 'bg-white/50'}`}
                        aria-label={`Go to slide ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>
                <div className="absolute -bottom-5 -right-5 bg-white p-4 rounded-lg shadow-lg">
                  <p className="text-dark font-bold">{t('home.hero.since')}</p>
                  <p className="text-gray-700">{t('home.hero.quality')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center text-white">{t('home.features.title')}</h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              {t('home.features.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <div className="bg-primary/10 inline-flex p-3 rounded-full mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2 dark:text-white">{t('home.features.feature3.title')}</h3>
              <p className="text-gray-600 dark:text-gray-300">
                {t('home.features.feature3.description')}
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <div className="bg-primary/10 inline-flex p-3 rounded-full mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2 dark:text-white">{t('home.features.feature2.title')}</h3>
              <p className="text-gray-600 dark:text-gray-300">
                {t('home.features.feature2.description')}
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <div className="bg-primary/10 inline-flex p-3 rounded-full mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2 dark:text-white">{t('home.features.feature1.title')}</h3>
              <p className="text-gray-600 dark:text-gray-300">
                {t('home.features.feature1.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section Preview */}
      <section className="py-16 bg-gray-100 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-8 md:mb-0">
              {/* Replace with actual about image */}
              <div className="bg-gray-300 h-80 rounded-lg overflow-hidden">
                <img 
                  src={imageLoader.getHeroImage('about')} 
                  alt="ÇelikSac Factory"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/800x600?text=ÇelikSac+Factory';
                  }}
                />
              </div>
            </div>
            <div className="md:w-1/2 md:pl-12">
              <h2 className="text-3xl font-bold mb-6 text-primary dark:text-white">{t('home.about.title')}</h2>
              <p className="text-gray-800 dark:text-gray-100 mb-6">
                {t('home.about.intro')}
              </p>
              <p className="text-gray-800 dark:text-gray-100 mb-6">
                {t('home.about.facilities')}
              </p>
              <ul className="mb-8">
                <li className="flex items-center mb-2">
                  <FaCheck className="text-primary dark:text-accent mr-2" />
                  <span className="text-gray-800 dark:text-gray-100">{t('home.about.point1')}</span>
                </li>
                <li className="flex items-center mb-2">
                  <FaCheck className="text-primary dark:text-accent mr-2" />
                  <span className="text-gray-800 dark:text-gray-100">{t('home.about.point2')}</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-primary dark:text-accent mr-2" />
                  <span className="text-gray-800 dark:text-gray-100">{t('home.about.point3')}</span>
                </li>
              </ul>
              <a href="/about" className="inline-flex items-center text-primary hover:text-blue-600 dark:text-accent dark:hover:text-accent/80 font-medium">
                {t('home.about.learn_more')}
                <FaArrowRight className="ml-2" size={14} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section Preview */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 dark:text-white">{t('home.products.title')}</h2>
            <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              {t('home.products.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Product Category 1 */}
            <div className="bg-white dark:bg-gray-700 rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow">
              <div className="h-48 bg-gray-300">
                <img 
                  src={imageLoader.getHomeProductImage('product1')} 
                  alt="Harrow Discs"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/400x300?text=Harrow+Discs';
                  }}
                />
              </div>
              <div className="p-4">
                <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">{t('home.products.product1.title')}</h3>
                <p className="text-gray-700 dark:text-gray-200 mb-4">{t('home.products.product1.description')}</p>
                <Link to="/products#harrow-discs" className="text-primary hover:text-primary/70 flex items-center group transition-colors">
                  {t('home.products.viewDetails')} <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={14} />
                </Link>
              </div>
            </div>

            {/* Product Category 2 */}
            <div className="bg-white dark:bg-gray-700 rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow">
              <div className="h-48 bg-gray-300">
                <img 
                  src={imageLoader.getHomeProductImage('product2')} 
                  alt="Seed Drill Discs"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/400x300?text=Seed+Drill+Discs';
                  }}
                />
              </div>
              <div className="p-4">
                <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">{t('home.products.product2.title')}</h3>
                <p className="text-gray-700 dark:text-gray-200 mb-4">{t('home.products.product2.description')}</p>
                <Link to="/products#seed-drill-discs" className="text-primary hover:text-primary/70 flex items-center group transition-colors">
                  {t('home.products.viewDetails')} <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={14} />
                </Link>
              </div>
            </div>

            {/* Product Category 3 */}
            <div className="bg-white dark:bg-gray-700 rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow">
              <div className="h-48 bg-gray-300">
                <img 
                  src={imageLoader.getHomeProductImage('product3')} 
                  alt="Plough Discs"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/400x300?text=Plough+Discs';
                  }}
                />
              </div>
              <div className="p-4">
                <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">{t('home.products.product3.title')}</h3>
                <p className="text-gray-700 dark:text-gray-200 mb-4">{t('home.products.product3.description')}</p>
                <Link to="/products#plough-discs" className="text-primary hover:text-primary/70 flex items-center group transition-colors">
                  {t('home.products.viewDetails')} <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={14} />
                </Link>
              </div>
            </div>

            {/* Product Category 4 */}
            <div className="bg-white dark:bg-gray-700 rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow">
              <div className="h-48 bg-gray-300">
                <img 
                  src={imageLoader.getHomeProductImage('product4')} 
                  alt="Notched Discs"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/400x300?text=Notched+Discs';
                  }}
                />
              </div>
              <div className="p-4">
                <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">{t('home.products.product4.title')}</h3>
                <p className="text-gray-700 dark:text-gray-200 mb-4">{t('home.products.product4.description')}</p>
                <Link to="/products#notched-discs" className="text-primary hover:text-primary/70 flex items-center group transition-colors">
                  {t('home.products.viewDetails')} <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={14} />
                </Link>
              </div>
            </div>
          </div>

          <div className="text-center mt-10">
            <Link 
              to="/products" 
              className="inline-flex items-center bg-primary hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-md transition-colors duration-300 group"
            >
              {t('home.products.viewAllButton')} <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-secondary py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">{t('home.cta.title')}</h2>
          <p className="text-white text-lg mb-8 max-w-2xl mx-auto">
            {t('home.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to="/contact" 
              className="bg-white hover:bg-gray-100 text-dark font-bold py-3 px-8 rounded-md transition-colors duration-300 group flex items-center justify-center"
            >
              {t('home.cta.contactButton')} <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={14} />
            </Link>
            <a 
              href="tel:+905544946406" 
              className="bg-transparent hover:bg-white/10 text-white border-2 border-white font-bold py-3 px-8 rounded-md transition-colors duration-300 flex items-center justify-center"
            >
              {t('home.cta.callButton')}
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
